import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Eye, EyeOff, ShieldCheck, KeyRound, Share2 } from 'lucide-react';
import toast from 'react-hot-toast';
import './Auth.css';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Normalize email/username: only normalize for non-email inputs (guest usernames)
      let normalizedEmail = email.trim();
      // Only normalize if it's not an email (doesn't contain @)
      if (!normalizedEmail.includes('@')) {
        normalizedEmail = normalizedEmail.replace(/\s+/g, '-').replace(/_/g, '-');
      }
      // Trim password to remove any accidental whitespace
      const normalizedPassword = password.trim();
      const result = await login(normalizedEmail, normalizedPassword);

      // Check if MFA is required
      if (result && 'requiresMFA' in result && result.requiresMFA) {
        // Store MFA token in sessionStorage as backup
        if (result.mfaToken) {
          sessionStorage.setItem('mfaToken', result.mfaToken);
        }
        // Redirect to MFA verification page
        navigate('/mfa/verify', { state: { mfaToken: result.mfaToken } });
        return;
      }

      // Check if MFA setup is required
      if (result && 'requiresMFASetup' in result && result.requiresMFASetup) {
        // Store setup token in sessionStorage as backup
        if (result.setupToken) {
          console.log('Storing setup token in sessionStorage');
          sessionStorage.setItem('mfaSetupToken', result.setupToken);
          console.log('Setup token stored, length:', result.setupToken.length);
        } else {
          console.error('No setupToken in result:', result);
        }
        // Redirect to MFA setup page
        console.log('Navigating to MFA setup with token:', result.setupToken ? result.setupToken.substring(0, 30) + '...' : 'NO TOKEN');
        navigate('/mfa/setup', { state: { setupToken: result.setupToken } });
        return;
      }

      // Check if MFA is optional
      if (result && 'mfaOptional' in result && result.mfaOptional) {
        // Store optional MFA token in sessionStorage
        if (result.mfaToken) {
          sessionStorage.setItem('mfaToken', result.mfaToken);
        }
        // Redirect to MFA verify page (which will have skip option)
        navigate('/mfa/verify', { state: { mfaToken: result.mfaToken, isOptional: true } });
        return;
      }

      // Normal login successful (result will be undefined or have success: true)
      if (!result || result.success) {
      toast.success('Login successful');
      navigate('/');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Login failed';
      const hint = error.response?.data?.hint;
      toast.error(errorMessage + (hint ? ` (${hint})` : ''));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-visual">
        <div className="auth-visual-brand">
          <span className="auth-visual-brand-icon">
            <Shield size={24} />
          </span>
          Password Wallet
        </div>
        <h2>Every credential your team relies on, kept in one vault.</h2>
        <p>
          Centralized, encrypted storage for servers, VPNs, kubeconfigs and application
          secrets — with full audit visibility for IT.
        </p>
        <ul className="auth-visual-features">
          <li><ShieldCheck size={18} /> End-to-end encrypted at rest</li>
          <li><KeyRound size={18} /> Multi-factor authentication built in</li>
          <li><Share2 size={18} /> Secure, time-limited sharing</li>
        </ul>
      </div>

      <div className="auth-panel">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-icon-badge">
              <Shield size={28} />
            </div>
            <h1>Welcome back</h1>
            <p>Sign in to access your IT credential vault</p>
          </div>
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-field">
              <label htmlFor="email">Email or Username</label>
              <div className="auth-input-wrapper">
                <input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder="you@company.com"
                />
              </div>
            </div>
            <div className="auth-field">
              <label htmlFor="password">Password</label>
              <div className="auth-input-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  className="has-toggle"
                />
                <button
                  type="button"
                  className="auth-input-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button type="submit" className="btn-primary auth-submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner" style={{ borderTopColor: 'white', borderColor: 'rgba(255,255,255,0.4)' }} />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
          <p className="auth-footer-note">Access is restricted to authorized IT personnel</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
